import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EditorContent } from '@tiptap/react';
import { Maximize2, Loader2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ComposeToolbar } from '@/features/compose/components/ComposeToolbar';
import { ComposeAttachments } from '@/features/compose/components/ComposeAttachments';
import { SignaturePicker } from '@/features/compose/components/SignaturePicker';
import { RecipientFields } from '@/features/compose/components/RecipientFields';
import { useComposeState } from '@/features/compose/hooks/useComposeState';
import { acquirePwaUpdateHold } from '@/lib/pwaUpdate';
import { AttachmentNagDialog } from '@/features/compose/components/AttachmentNagDialog';
import { useContactStore } from '@/stores/contactStore';
import { useSignatureStore } from '@/stores/signatureStore';
import { useAccountStore } from '@/stores/accountStore';
import { useUIStore } from '@/stores/uiStore';
import { UNIFIED_ACCOUNT_ID } from '@/constants/mailbox';
import type { ReplyContext } from '@/types/ui';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface InlineReplyComposerProps {
  context: ReplyContext;
  accountId: string | null;
  onClose: () => void;
  onSent?: () => void;
  onOpenFullCompose?: (context: ReplyContext) => void;
}

export function InlineReplyComposer({
  context,
  accountId,
  onClose,
  onSent,
  onOpenFullCompose,
}: InlineReplyComposerProps) {
  const signatures = useSignatureStore((state) => state.signatures);
  const accounts = useAccountStore((state) => state.accounts);
  const contacts = useContactStore((state) => state.contacts);
  // The unified "All accounts" entry (id = 'all') is a synthetic view, not a
  // real sending identity. Exclude it from the From selector and from the
  // compose hook's account fallbacks, mirroring ComposeModalHost.
  const composeAccounts = useMemo(
    () => accounts.filter((account) => account.id !== UNIFIED_ACCOUNT_ID),
    [accounts],
  );
  const ensureUnifiedContacts = useContactStore((state) => state.ensureUnifiedContacts);
  const setSettingsDialogOpen = useUIStore((state) => state.setSettingsDialogOpen);
  const setSettingsInitialTab = useUIStore((state) => state.setSettingsInitialTab);

  // The inline reply/forward path renders outside ComposeModalHost, so nothing
  // else loads contacts here. Without this, recipient autocomplete is always
  // empty ("to suggestions don't populate") for replies and forwards.
  useEffect(() => {
    void ensureUnifiedContacts().catch((error) => {
      console.error('Failed to load contacts for inline composer:', error);
    });
  }, [ensureUnifiedContacts]);

  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [closeAction, setCloseAction] = useState<'keep' | 'discard' | null>(null);

  const compose = useComposeState({
    open: true,
    onOpenChange: () => {},
    replyTo: context,
    accounts: composeAccounts,
    accountId: accountId ?? undefined,
    contacts,
    signatures,
    mode: 'inline',
    onSent,
    onClose,
  });

  const {
    editor,
    to,
    cc,
    bcc,
    subject,
    fromAccountId,
    showCc,
    showBcc,
    attachments,
    sending,
    focusedField,
    suggestedContacts,
    selectedSignatureId,
    setTo,
    setCc,
    setBcc,
    setShowCc,
    setShowBcc,
    setFocusedField,
    handleAccountChange,
    handleSend,
    handleFileSelect,
    appendAttachments,
    removeAttachment,
    applyContactSuggestion,
    handleSignatureSelect,
    insertInlineImageFromFile,
  } = compose;

  const modeLabel = useMemo(() => {
    switch (context.mode) {
      case 'replyAll':
        return 'Reply all';
      case 'forward':
        return 'Forward';
      default:
        return 'Reply';
    }
  }, [context.mode]);

  const handleManageSignatures = useCallback(() => {
    setSettingsInitialTab('signatures');
    setSettingsDialogOpen(true);
  }, [setSettingsDialogOpen, setSettingsInitialTab]);

  const handleOpenFullComposer = useCallback(() => {
    if (!onOpenFullCompose) return;
    const updatedContext: ReplyContext = {
      ...context,
      to,
      cc: showCc && cc.trim() ? cc.trim() : undefined,
      subject: context.subject,
      draftBody: editor?.getHTML() || undefined,
      // The full composer must continue THIS session: same From account and
      // the SAME draft (otherwise its autosave creates a duplicate) (G1/G2).
      accountId: fromAccountId ?? context.accountId ?? null,
      _expandState: {
        attachments: attachments.length > 0 ? attachments : undefined,
        selectedSignatureId,
        showBcc,
        bcc: showBcc ? bcc : undefined,
        draftId: compose.draftId ?? null,
        fromAccountId: fromAccountId ?? null,
      },
    };
    onOpenFullCompose(updatedContext);
  }, [onOpenFullCompose, context, to, cc, showCc, bcc, showBcc, editor, attachments, selectedSignatureId, fromAccountId, compose.draftId]);

  const shouldConfirmClose = !compose.sending && (compose.draftId || compose.draftDirty || compose.hasUserModified);

  // G12: navigation can unmount the inline composer without any close flow
  // (selecting another email clears the context). Flush a final save so
  // everything since the last 800ms autosave survives; never-saved content
  // gets its draft created by the same force-save.
  const composeRef = useRef(compose);
  composeRef.current = compose;
  useEffect(() => {
    return () => {
      const current = composeRef.current;
      if (!current.sending && (current.draftDirty || current.hasUserModified)) {
        void current.forceSaveDraft();
      }
    };
  }, []);

  // A silent app update reloads the page, which would unmount this composer
  // and drop the last ≤800ms of typing; hold updates off while it's mounted.
  useEffect(() => acquirePwaUpdateHold(), []);

  const handleCancelClick = useCallback(() => {
    if (shouldConfirmClose) {
      setCloseConfirmOpen(true);
      return;
    }
    onClose();
  }, [shouldConfirmClose, onClose]);

  const handleKeepDraftAndClose = useCallback(async () => {
    try {
      setCloseAction('keep');
      const saved = await compose.forceSaveDraft();
      if (!saved) {
        // Final save failed — keep the composer open instead of silently
        // dropping the edits (G8).
        return;
      }
      setCloseConfirmOpen(false);
      onClose();
    } finally {
      setCloseAction(null);
    }
  }, [compose, onClose]);

  const handleDiscardDraftAndClose = useCallback(async () => {
    setCloseAction('discard');
    const discarded = await compose.handleDiscardDraft();
    setCloseAction(null);
    if (discarded) {
      setCloseConfirmOpen(false);
      onClose();
    }
  }, [compose, onClose]);

  if (!editor) return null;

  return (
    <>
      <div className="glass-surface rounded-xl p-2 sm:p-3">
        <div className="flex flex-wrap items-center gap-1">
          <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-primary">{modeLabel}</span>
          <ComposeToolbar editor={editor} onInsertInlineImage={insertInlineImageFromFile} variant="inline" />
          <div className="ml-auto flex shrink-0 items-center gap-1">
            <div className="hidden sm:flex items-center gap-1">
              {composeAccounts.length > 1 && (
                <Select value={fromAccountId ?? undefined} onValueChange={handleAccountChange}>
                  <SelectTrigger
                    tabIndex={-1}
                    className="h-7 px-2 text-xs gap-1 max-w-[250px]"
                    aria-label="Sender account"
                  >
                    <SelectValue placeholder="From" />
                  </SelectTrigger>
                  <SelectContent>
                    {composeAccounts.map((account) => (
                      <SelectItem key={account.id} value={account.id}>
                        <div className="flex items-baseline gap-2">
                          <span className="text-xs font-medium">
                            {account.displayName || account.emailAddress}
                          </span>
                          {account.displayName && (
                            <span className="text-xs text-muted-foreground">
                              {account.emailAddress}
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <SignaturePicker
                signatures={signatures}
                selectedSignatureId={selectedSignatureId}
                onSelect={handleSignatureSelect}
                onManage={handleManageSignatures}
              />
              <ComposeAttachments
                attachments={attachments}
                onRemoveAttachment={removeAttachment}
                onFileInputChange={handleFileSelect}
                onAppendFiles={appendAttachments}
                showList={false}
                showDropzone={true}
              />
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleOpenFullComposer}
                  disabled={sending}
                  tabIndex={-1}
                  className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                >
                  <Maximize2 className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Full composer</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleCancelClick}
                  disabled={sending}
                  tabIndex={-1}
                  className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                >
                  <XCircle className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Cancel</TooltipContent>
            </Tooltip>
            <Button
              type="button"
              onClick={handleSend}
              disabled={sending || !editor}
              className="h-7 gap-1 text-xs font-semibold px-3"
            >
              {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Send'}
            </Button>
          </div>
        </div>

        {compose.draftError && (
          <p className="mt-1 text-xs text-destructive">{compose.draftError}</p>
        )}

        <div className="mt-2 space-y-2">
          <RecipientFields
            to={to}
            cc={cc}
            bcc={bcc}
            onToChange={setTo}
            onCcChange={setCc}
            onBccChange={setBcc}
            showCc={showCc}
            showBcc={showBcc}
            onShowCc={() => setShowCc(true)}
            onShowBcc={() => setShowBcc(true)}
            onHideCc={() => {
              setShowCc(false);
              setCc('');
            }}
            onHideBcc={() => {
              setShowBcc(false);
              setBcc('');
            }}
            suggestedContacts={suggestedContacts}
            focusedField={focusedField}
            onFocusField={setFocusedField}
            onActiveInputChange={compose.setActiveInputValue}
            applyContactSuggestion={applyContactSuggestion}
          />
          <input type="hidden" value={subject} readOnly aria-hidden="true" />
        </div>

        <div className="mt-2 space-y-2">
          <div className="rounded-2xl border border-border/60 bg-background/95 px-5 py-4 shadow-inner [&_.quoted-content-block]:hidden">
            <EditorContent editor={editor} />
          </div>
          <ComposeAttachments
            attachments={attachments}
            onRemoveAttachment={removeAttachment}
            onFileInputChange={handleFileSelect}
            onAppendFiles={appendAttachments}
            showList={true}
            showDropzone={false}
          />
        </div>
      </div>

      <AlertDialog
        open={closeConfirmOpen}
        onOpenChange={(open) => {
          if (!open && closeAction === null) {
            setCloseConfirmOpen(false);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Close reply?</AlertDialogTitle>
            <AlertDialogDescription>Keep this draft for later or discard it?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <AlertDialogCancel
              disabled={closeAction !== null || compose.draftSaving}
              onClick={() => setCloseConfirmOpen(false)}
            >
              Cancel
            </AlertDialogCancel>
            <Button
              variant="destructive"
              size="sm"
              disabled={closeAction !== null || compose.draftSaving}
              onClick={handleDiscardDraftAndClose}
            >
              {closeAction === 'discard' ? 'Discarding\u2026' : 'Discard draft'}
            </Button>
            <AlertDialogAction
              disabled={closeAction !== null || compose.draftSaving}
              onClick={handleKeepDraftAndClose}
            >
              {closeAction === 'keep' ? 'Saving\u2026' : 'Keep draft'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AttachmentNagDialog
        open={compose.attachmentNagOpen}
        onCancel={compose.dismissAttachmentNag}
        onConfirm={compose.confirmSendWithoutAttachments}
      />
    </>
  );
}
